import { Link } from 'react-router-dom';
import { MessageCircle, Package } from 'lucide-react';
import { formatINR } from '../../../utils/format';
import { contactInfo, getHoursSentence, getWhatsAppUrl } from '../../../configs/contactInfo';
import { useSiteConfig } from '../../../configs/siteConfig';

const isNonZero = value => Number(value) > 0;

/** Right-hand summary on the payment page: what was ordered and what it costs (from the order itself). */
const OrderSummaryCard = ({ order, orderId }) => {
  const item = order?.items?.[0];
  const subtotal = order?.subTotal ?? item?.unitPrice;
  const taxLabel = 'GST';

  return (
    <div className="pk-stack">
      <div className="pk-card co-summary">
        <h2 className="pk-card__title co-card-title">Order summary</h2>

        {item && (
          <div className="co-mini">
            <span className="co-mini__media">
              {item.productImage ? (
                <img src={item.productImage} alt="" loading="lazy" />
              ) : (
                <Package aria-hidden="true" />
              )}
            </span>
            <span className="co-mini__name">{item.productName}</span>
          </div>
        )}

        <dl className="pk-rows">
          <div>
            <dt>Order number</dt>
            <dd className="co-mono">{orderId}</dd>
          </div>
          {subtotal != null && (
            <div>
              <dt>Subtotal</dt>
              <dd>{formatINR(subtotal)}</dd>
            </div>
          )}
          {isNonZero(order?.feeAmount) && (
            <div>
              <dt>Platform fee</dt>
              <dd>{formatINR(order.feeAmount)}</dd>
            </div>
          )}
          {isNonZero(order?.discountAmount) && (
            <div>
              <dt>Discount</dt>
              <dd>-{formatINR(order.discountAmount)}</dd>
            </div>
          )}
          {isNonZero(order?.taxAmount) && !order?.pricesIncludeTax && (
            <div>
              <dt>
                {taxLabel}
                {isNonZero(order?.taxRatePercent) && ` (${order.taxRatePercent}%)`}
              </dt>
              <dd>{formatINR(order.taxAmount)}</dd>
            </div>
          )}
          <div className="pk-rows__total">
            <dt>Total</dt>
            <dd>{formatINR(order?.totalAmount)}</dd>
          </div>
        </dl>
        {isNonZero(order?.taxAmount) && order?.pricesIncludeTax && (
          <p className="pk-hint co-summary__note">
            Includes {formatINR(order.taxAmount)} {taxLabel}
          </p>
        )}
        <p className="pk-hint co-summary__note">
          All sales are final. See the{' '}
          <Link to="/refund" className="pk-link">
            refund policy
          </Link>
          .
        </p>
        <Link
          to={`/profile/order-details/${encodeURIComponent(orderId)}`}
          className="pk-btn pk-btn--secondary pk-btn--sm pk-btn--block co-summary__cta"
        >
          View order
        </Link>
      </div>

      <SupportCard />
    </div>
  );
};

/** Support contact (the only channel there is today: WhatsApp / phone). */
export const SupportCard = () => {
  useSiteConfig();
  return (
    <div className="pk-card pk-card--muted co-support">
      <MessageCircle aria-hidden="true" />
      <p>
        Need help with a payment? Message us on{' '}
        <a
          className="pk-link"
          href={getWhatsAppUrl()}
          target="_blank"
          rel="noopener noreferrer"
        >
          WhatsApp
        </a>{' '}
        or call {contactInfo.phone.display}, {getHoursSentence()}.
      </p>
    </div>
  );
};

export default OrderSummaryCard;
